import { Skeleton } from "@mantine/core";
import { useLocation, useParams } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { useGetCatalog } from "../../../api/generated/endpoints/generation/generation";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import {
  arrivingTalkingHeadActor,
  tookTalkingHeadActor,
} from "../../../platform/actors/talking-head-handoff";
import { TalkingHeadForm } from "../../../platform/studio/tools/talking-head/TalkingHeadForm";
import { CreateFrame } from "./CreateFrame";
import classes from "./create.module.css";
import { RecentResults } from "./RecentResults";

/** The prototype's "Recent talking head results": the Studio's Talking Head jobs, both kinds. */
const TALKING_TYPES = ["video.talking_head", "video.talking_head_advanced"] as const;
const NO_EVENT = () => undefined;

/**
 * The prototype's `#create-talking`: the Studio's Talking Head form in the composer card (its two
 * styles the form's Advanced off and on), then the recent talking head results.
 */
export function CreateTalkingScreen() {
  const { teamId, brandId } = useParams({
    from: "/signed-in/teams/$teamId/brands/$brandId/home2/create-talking",
  });
  const catalog = useGetCatalog();
  // An actor's "Talking head" (the redesign's Actors page) brings it here once, as the Studio's
  // Talking Head takes one: the navigation's actor, marked taken so a reload starts without it.
  const location = useLocation();
  const [arrival] = useState(() => arrivingTalkingHeadActor(location));
  useEffect(() => {
    if (arrival !== null) tookTalkingHeadActor(location);
  }, [arrival, location]);
  const offered = catalog.data?.video_models.some((each) =>
    each.job_types.includes("video.talking_head"),
  );
  const composer = catalog.isError ? (
    <ErrorAlert error={catalog.error} />
  ) : catalog.data === undefined ? (
    <Skeleton className={classes.composerSkeleton} />
  ) : offered === true ? (
    // A brand's own draft, as the Studio's composer is keyed (decision 20).
    <TalkingHeadForm
      key={brandId}
      catalog={catalog.data}
      above={undefined}
      overlay={undefined}
      onStarted={NO_EVENT}
      actor={arrival}
      inline
      styles
    />
  ) : (
    <p className={classes.dim}>No model makes talking heads yet.</p>
  );
  return (
    <CreateFrame
      composer={composer}
      results={
        <RecentResults teamId={teamId} brandId={brandId} types={TALKING_TYPES} noun="talking head" />
      }
    />
  );
}
